import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

import {
  ApiImporttasksList200Response,
  ImportTask,
  ImportTaskStatusEnum,
} from "@nens/brostar/openapi-client";
import { OurUseQueryOptions } from "@nens/react-query-utils";
import { removeNullUndefined } from "@nens/shared-utils";

import { getApi } from "@/api/api-configuration";

import { useUser } from "../providers/auth-provider";

import { queryDirectly } from "./helpers/query-directly";

const api = getApi();

export const importTaskIsProcessing = (task: ImportTask) =>
  task.status === ImportTaskStatusEnum.Pending || task.status === ImportTaskStatusEnum.Processing;

// ─── LIST ────────────────────────────────────────────────────────────────────

const getImportTaskListQuery = (
  kvkNumber: string
): OurUseQueryOptions<ApiImporttasksList200Response> => ({
  queryKey: ["import-task-list", kvkNumber],
  queryFn: async () => {
    const response = await api.apiImporttasksList({ kvkNumber });
    const broDomains: string[] = [];
    return {
      ...response,
      // Remove duplicate broDomains, as sometimes there can be multiple import tasks for the same broDomain
      // And if an old one hangs in processing, we don't want to show the broDomain as loading twice in the UI
      results: response.results.filter(({ broDomain }) => {
        if (!broDomain) return false;
        if (!broDomains.includes(broDomain)) {
          broDomains.push(broDomain);
          return true;
        }
        return false;
      }),
    };
  },
  refetchInterval: (query) => {
    const results = query.state.data?.results;
    if (!results) return false;

    const hasActiveImports = results.some(importTaskIsProcessing);

    return hasActiveImports ? 10000 : false;
  },
});

export const useImportTaskListQueryDirectly = () => {
  const queryClient = useQueryClient();
  const { kvk } = useUser();

  return () => queryDirectly(queryClient, getImportTaskListQuery(kvk));
};

/**
 * Hook that monitors import tasks for a specific kvkNumber.
 * Automatically refetches every 5 seconds if any tasks are PENDING or PROCESSING.
 * Returns which broDomains are currently loading.
 */
export const useImportTasksLoadingStatus = () => {
  const { kvk } = useUser();
  const query = useQuery(getImportTaskListQuery(kvk));

  const { loadingDomains, progress } = useMemo(() => {
    if (!query.data?.results) return { loadingDomains: [], progress: undefined };

    const processingTasks = query.data.results.filter(importTaskIsProcessing);

    const loadingDomains = processingTasks.map((task) => task.broDomain);

    const progressArray = removeNullUndefined(processingTasks.map((t) => t.progress));
    if (progressArray.length === 0) return { loadingDomains, progress: undefined };
    const progress = Number(
      (progressArray.reduce((a, b) => a + b, 0) / progressArray.length).toFixed(0)
    );

    return { loadingDomains, progress };
  }, [query.data?.results]);
  return {
    loadingDomains,
    isAnyLoading: loadingDomains.length > 0,
    isLoading: query.isLoading,
    progress,
  };
};

/**
 * Hook that returns the last import date for a specific kvkNumber.
 * Uses the same query as useImportTasksLoadingStatus, so they share the cache.
 */
export const useLastImportDate = () => {
  const { kvk } = useUser();
  const query = useQuery(getImportTaskListQuery(kvk));

  const lastImportDate = useMemo(() => {
    if (!query.data?.results) return null;

    return (
      query.data.results
        .map((t) => t.updated ?? t.created)
        .filter((date): date is Date => date instanceof Date)[0] ?? null
    );
  }, [query.data?.results]);

  return {
    lastImportDate,
    isLoading: query.isLoading,
  };
};

// ─── INVALIDATION ────────────────────────────────────────────────────────────

export const useInvalidateImportTaskListQuery = () => {
  const queryClient = useQueryClient();
  const { kvk } = useUser();

  return async () => {
    await queryClient.invalidateQueries({
      queryKey: getImportTaskListQuery(kvk).queryKey,
      refetchType: "all",
    });
  };
};
